import React, { useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import {
  IAddressData,
  IChallengeCheckoutPaymentProduct,
  IUser,
} from 'src/shared/models';
import {
  capitalizeFirstLetter,
  customFormatMessage,
  getRegionName,
  getRegions,
} from 'utils';
import { PlatformEnum } from 'src/shared/enums';
import Link from 'src/components/Link';
import routePaths from 'config/routes';
import { config } from 'config/config';
import moment from 'moment';
import getConfig from 'next/config';

interface OwnProps {
  checkoutName: string;
  product?: IChallengeCheckoutPaymentProduct;
  singleProduct: boolean;
  user: IUser;
  selectedAddress: IAddressData;
  taxLabel: number;
  checkoutData;
  paymentMethod: string;
  cardNumber: number;
}

const { PLATFORM } = config.APP;
const { publicRuntimeConfig } = getConfig();

export default function CheckoutPaymentEnd({
  checkoutName,
  product,
  singleProduct,
  user,
  selectedAddress,
  taxLabel,
  checkoutData,
  paymentMethod,
  cardNumber,
}: OwnProps) {
  const { formatMessage } = useIntl();
  const [regionName, setRegionName] = useState<string>();

  useEffect(() => {
    fetchInitialData();
  }, []);

  async function fetchInitialData() {
    const regions = await getRegions();
    setRegionName(getRegionName(regions, selectedAddress.regionName));
  }

  const pointsLabel = Math.abs(product?.score);
  const moneyLabel = Math.abs(product?.price);

  const getTitle = () => {
    if (singleProduct) return product?.lead;
    return `${capitalizeFirstLetter(checkoutName)} ${product?.lead}`;
  };

  return (
    <div className="checkout-payment-end">
      <div className="checkout-payment-end__col">
        <div className="checkout-payment-end__align-text">
          <div className="icon icon--process-check" />
          <h3 className="checkout-payment-end__title fs-mask">
            {formatMessage(
              {
                id: 'page.experience.checkout.end.title',
              },
              { userName: user?.salesforce?.userFirstname },
            )}
          </h3>
          <p className="checkout-payment-end__points fs-mask">
            <FormattedMessage
              id="page.challenge.payment.{userEmail}-confirmation"
              values={{
                strong: (chunks: string) => <strong>{chunks}</strong>,
                userEmail: user?.email,
              }}
            />
          </p>
          <Link href={routePaths.PAGES.EXPERIENCE}>
            <a className="btn-primary btn-primary--black btn-primary__small checkout-payment-end__button">
              {formatMessage({
                id: 'page.experience.checkout.end.experiences',
              })}
            </a>
          </Link>
        </div>
        <hr className="payment-underline" />
        <h3 className="checkout-payment-end__details__title">
          {formatMessage({
            id: 'page.challenge.payment.shipment-details',
          })}
        </h3>
        <div className="checkout-payment-end__details">
          <div>
            <div className="checkout-payment-end__details__detail-title">
              {formatMessage({
                id: 'page.challenge.payment.shipment-details.shipment-number',
              })}
            </div>
            <div className="checkout-payment-end__details__detail-item fs-mask">
              {`${checkoutData?.BSTKD}`}
            </div>
            <div className="checkout-payment-end__details__detail-title">
              {formatMessage({
                id: 'page.challenge.payment.shipment-details.shipment-date',
              })}
            </div>
            <div className="checkout-payment-end__details__detail-item">
              {`${moment(checkoutData?.BSTDK).format('DD/MM/YYYY')}`}
            </div>
            <div className="checkout-payment-end__details__detail-title">
              {formatMessage({
                id: 'page.challenge.payment.shipment-details.shipment-email',
              })}
            </div>
            <div className="checkout-payment-end__details__detail-item fs-mask">
              {user.email}
            </div>
          </div>
          <div className="checkout-payment-end__col__margin">
            <div className="checkout-payment-end__details__detail-title">
              {formatMessage({
                id: 'page.challenge.payment.shipment-details.shipment-shipment-data',
              })}
            </div>
            <div className="checkout-payment-end__details__detail-item fs-mask">
              <div>{selectedAddress.name + ' ' + selectedAddress.surname}</div>
              <div>{selectedAddress.phone}</div>
              <div>
                {selectedAddress.address.includes('#')
                  ? selectedAddress.address.substring(
                      0,
                      selectedAddress.address.indexOf('#'),
                    )
                  : selectedAddress.address}
              </div>
              <div>
                {selectedAddress.zipCode +
                  ', ' +
                  selectedAddress.city +
                  ', ' +
                  regionName}
              </div>
              <div>
                {PLATFORM === PlatformEnum.CON_SPAIN
                  ? formatMessage({
                      id: 'menu.dropdown.country.espana',
                    })
                  : null}
              </div>
            </div>
            <div className="checkout-payment-end__details__detail-title">
              {formatMessage({
                id: 'page.challenge.payment.shipment-details.shipment-method',
              })}
            </div>
            <div className="checkout-payment-end__details__detail-item">
              <div>
                {formatMessage({
                  id: 'page.experience.checkout.summary-points.standard',
                })}
              </div>
              <div className="checkout-payment-end__details__detail-item-amount">
                {formatMessage({
                  id: 'page.experience.checkout.summary-points.free',
                })}
              </div>
            </div>
            {paymentMethod === 'card' && (
              <>
                <div className="checkout-payment-end__details__detail-title">
                  {formatMessage({
                    id: 'page.challenge.payment.shipment-details.shipment-payment-method',
                  })}
                </div>

                <div className="checkout-payment-end__details__detail-last-item fs-mask">
                  <div
                    className={`icon icon--credit_card_payment checkout-payment-end__details__detail-last-item__icon`}
                  />
                  <FormattedMessage
                    id="page.challenge.payment.shipment-details.card-method"
                    values={{
                      numberCard: cardNumber,
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <hr className="payment-underline" />
        <h3 className="checkout-payment-end__details__title">
          {formatMessage({
            id: 'page.experience.checkout.summary-label',
          })}
        </h3>

        <div className="checkout-payment-product">
          <div className="checkout-payment-product__header">
            <div className="checkout-payment-product__header--image">
              <img alt="checkout-img" src={product?.img} />
            </div>
            <span className="checkout-payment-product__header--product-name">
              {getTitle()}
            </span>
          </div>
          <div className="checkout-payment-product__description">
            <div className="checkout-payment-product__description--content">
              {product?.description}
            </div>
          </div>
        </div>
        <div className="checkout-payment-product__product-wrapper">
          <div className="payment-subtotal">
            <span className="payment-subtotal-titles">
              {formatMessage({
                id: 'page.experience.checkout.subtotal-points',
              })}
            </span>
            <span className="payment-subtotal-amount">
              {formatMessage(
                { id: 'page.experience.checkout.subtotal-points.points' },
                { points: pointsLabel },
              )}
            </span>
          </div>
          <div className="payment-subtotal">
            <span className="payment-subtotal-titles">
              {formatMessage({
                id: 'page.experience.checkout.subtotal-money',
              })}
            </span>
            <span className="payment-subtotal-amount">
              {formatMessage(
                { id: 'page.experience.checkout.summary-points.euros' },
                { moneyAmount: moneyLabel },
              )}
            </span>
          </div>
          <div className="payment-subtotal">
            <span className="payment-subtotal-titles">
              {formatMessage({
                id: 'page.experience.checkout.iva',
              })}
            </span>
            <span className="payment-subtotal-amount">
              {formatMessage(
                { id: 'page.experience.checkout.summary-points.euros' },
                { moneyAmount: taxLabel },
              )}
            </span>
          </div>
          <div className="payment-shipment">
            <div className="payment-shipment-days">
              {formatMessage({ id: 'page.experience.checkout.shipment' })}
            </div>
            <div className="payment-shipment-amount">
              {formatMessage({
                id: 'page.experience.checkout.summary-points.free',
              })}
            </div>
          </div>
          <div className="payment-shipment">
            <div className="payment-shipment-regions">
              {formatMessage({
                id: 'page.experience.checkout.shipment-exception',
              })}
            </div>
          </div>
          <div className="payment-total">
            <span className="payment-subtotal-titles">
              {formatMessage({
                id: 'page.experience.checkout.total',
              })}
            </span>
            <span className="payment-total-money-points">
              {formatMessage(
                {
                  id: 'page.experience.checkout.summary-points.points-euros',
                },
                { points: pointsLabel, moneyAmount: moneyLabel },
              )}
            </span>
          </div>
        </div>
        <hr className="payment-underline" />
        <span className="checkout-payment-end__bill">
          <div className="icon icon--document checkout-payment-end__bill__bill-icon" />
          <span>
            {formatMessage({
              id: 'page.challenge.payment.shipment-details.shipment-bill',
            })}
            <a
              href={`${publicRuntimeConfig.PLATFORM_PREFIX || ''}${
                routePaths.FOOTER.CONTACT_US
              }`}
              className="checkout-payment-end__bill__contact">
              {customFormatMessage(
                'page.challenge.payment.shipment-details.shipment-bill-contact',
              )}
            </a>
          </span>
        </span>
      </div>
    </div>
  );
}
